import { useState } from 'react';
import type { ReactNode } from 'react';
import { usePairingCode, useSetWhatsAppTransport, useWhatsAppTransport } from '../api/queries';
import type { WhatsAppTransportKind } from '../api/types';
import ui from '../components/ui.module.css';
import { errorMessage } from '../copy';
import styles from './WhatsApp.module.css';

/**
 * WhatsApp: which channel the login codes travel through.
 *
 *   - Oficial (Cloud API): Meta's WhatsApp Business Platform. Needs its
 *     credentials on the server; the only one fit for production.
 *   - Baileys: the unofficial WhatsApp Web client. Against WhatsApp's Terms of
 *     Service — the number can be banned. Linked here with a pairing code.
 *
 * The worker stores the choice, so it survives a restart.
 */

const OPTIONS: { kind: WhatsAppTransportKind; title: string; detail: string }[] = [
  {
    kind: 'cloud',
    title: 'WhatsApp oficial (Cloud API)',
    detail: 'API oficial da Meta. Recomendado para produção.',
  },
  {
    kind: 'baileys',
    title: 'Baileys (não oficial)',
    detail: 'Cliente WhatsApp Web não oficial. Viola os Termos do WhatsApp — o número pode ser banido.',
  },
];

const STATUS: Record<string, string> = {
  connected: 'Conectado',
  connecting: 'Conectando…',
  disconnected: 'Desconectado',
  logged_out: 'Desvinculado — gere um novo código de pareamento',
};

export function WhatsApp(): ReactNode {
  const [linking, setLinking] = useState(false);
  const { data, isLoading, error } = useWhatsAppTransport(linking);
  const change = useSetWhatsAppTransport();
  const pairing = usePairingCode();
  const [failure, setFailure] = useState<string | null>(null);
  const [phone, setPhone] = useState('');

  const baileysOffline = data?.kind === 'baileys' && data.status !== 'connected';

  const select = (kind: WhatsAppTransportKind) => {
    if (!data || kind === data.kind) return;
    if (
      kind === 'baileys' &&
      !window.confirm('Baileys é um cliente não oficial e viola os Termos do WhatsApp. O número pode ser banido. Continuar?')
    ) {
      return;
    }
    setFailure(null);
    setLinking(false);
    pairing.reset();
    change.mutate(kind, { onError: (cause) => setFailure(errorMessage(cause)) });
  };

  const e164 = `+${phone.replace(/\D/g, '')}`;

  return (
    <section className={ui.panel}>
      <h1 className={ui.h1}>WhatsApp</h1>
      <p className={styles.lead}>Canal usado para o login dos eleitores pelo WhatsApp.</p>

      {error ? <p className={ui.error}>{errorMessage(error)}</p> : null}
      {failure ? <p className={ui.error}>{failure}</p> : null}
      {isLoading ? <p className={ui.muted}>Carregando…</p> : null}

      {data ? (
        <>
          <div role="radiogroup" aria-label="Canal do WhatsApp" className={styles.options}>
            {OPTIONS.map((option) => {
              const available = data.available.includes(option.kind);
              const active = data.kind === option.kind;
              return (
                <label
                  key={option.kind}
                  className={`${styles.option} ${active ? styles.optionActive : ''} ${available ? '' : styles.optionOff}`}
                >
                  <input
                    type="radio"
                    name="transport"
                    value={option.kind}
                    checked={active}
                    disabled={!available || change.isPending}
                    onChange={() => select(option.kind)}
                  />
                  <span>
                    <strong>{option.title}</strong>
                    <span className={styles.detail}>
                      {available ? option.detail : 'Credenciais da Meta não configuradas no servidor.'}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>

          <p className={data.status === 'connected' ? styles.ok : styles.alert}>
            {STATUS[data.status] ?? data.status}
            {data.phone ? ` — ${data.phone}` : ''}
          </p>

          {baileysOffline ? (
            <div className={styles.pair}>
              <h2 className={ui.h2}>Vincular número</h2>
              <p className={styles.lead}>
                Gere o código e digite no celular: WhatsApp → Aparelhos conectados → Conectar aparelho → Conectar
                com número de telefone.
              </p>
              <div className={styles.form}>
                <label className={ui.field}>
                  <span className={ui.label}>Número (com DDI e DDD)</span>
                  <input
                    className={ui.input}
                    inputMode="tel"
                    placeholder="+55 66 99999-0000"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className={ui.primary}
                  disabled={!/^\+[0-9]{10,15}$/.test(e164) || pairing.isPending}
                  onClick={() => {
                    setFailure(null);
                    pairing.mutate(e164, {
                      onSuccess: () => setLinking(true),
                      onError: (cause) => setFailure(errorMessage(cause)),
                    });
                  }}
                >
                  Gerar código
                </button>
              </div>
              {pairing.data ? (
                <p className={styles.code} aria-label="Código de pareamento">
                  {pairing.data}
                </p>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
